import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { Users, Crown, Check } from 'lucide-react';

interface TeamBranding {
  id: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  is_shared_with_team: boolean;
  created_by: string;
  creator?: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface TeamBrandingSelectorProps {
  selectedBrandingId?: string;
  onSelect: (brandingId: string | null) => void;
  currentUserId: string;
}

export function TeamBrandingSelector({ selectedBrandingId, onSelect, currentUserId }: TeamBrandingSelectorProps) {
  const { session } = useAuth();
  const [teamBrandings, setTeamBrandings] = useState<TeamBranding[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);
  
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  useEffect(() => {
    loadTeamBrandings();
  }, [session]);

  const loadTeamBrandings = async () => {
    if (!session?.access_token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/branding/team`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeamBrandings(data);
      }
    } catch (error) {
      console.error('Error loading team brandings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (teamBrandings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-900">Team-Branding verwenden</span>
        </div>
        <span className="text-sm text-blue-600">
          {teamBrandings.length} verfügbar
        </span>
      </button>

      {showSelector && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Verfügbare Team-Brandings</h4>
          
          {teamBrandings.map((branding) => (
            <div
              key={branding.id}
              onClick={() => {
                onSelect(branding.id);
                setShowSelector(false);
              }}
              className={`
                p-3 border rounded-lg cursor-pointer transition-all
                ${selectedBrandingId === branding.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  {branding.logo_url && (
                    <img 
                      src={branding.logo_url} 
                      alt={branding.company_name}
                      className="h-8 w-auto object-contain"
                    />
                  )}
                  <div>
                    <h5 className="font-medium text-gray-900">{branding.company_name}</h5>
                    <p className="text-sm text-gray-500">
                      von {branding.creator?.full_name || branding.creator?.email || 'Team-Mitglied'}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: branding.primary_color }}
                      />
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: branding.secondary_color }}
                      />
                    </div>
                  </div>
                </div>
                {selectedBrandingId === branding.id && (
                  <Check className="w-5 h-5 text-blue-600" />
                )}
              </div>
              
              {branding.created_by === currentUserId && (
                <div className="mt-2 flex items-center space-x-1 text-xs text-gray-500">
                  <Crown className="w-3 h-3" />
                  <span>Dein Branding</span>
                </div>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setShowSelector(false);
            }}
            className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
          >
            Kein Team-Branding verwenden
          </button>
        </div>
      )}
    </div>
  );
} 